import { tryResolveUnconfirmedUsername } from '@/lib/resolve-username.js'
import {
  SUPPORT_SESSION_AT_LOAD,
  type SupportSession,
} from '@/lib/support-session.js'
import { Amplify } from 'aws-amplify'
import { AuthError } from 'aws-amplify/auth'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import * as Auth from '@aws-amplify/auth'

import { IS_LOCAL_API, LOCAL_USER, LOCAL_USER_ID } from '@droidmap/frontend-env'
import { StandardOrgIds } from '@droidmap/shared-service-contract'
import { User } from '@droidmap/user-service-contract'

import { createStandaloneUserServiceClient } from './ClientContext.js'
import { useConfig } from './ConfigContext.js'
import { SupportSessionProvider } from './SupportSessionContext.js'

const ACCESS_TOKEN_REFRESH_BUFFER_MS = 60 * 1000

/**
 * Sanitizes error messages to remove sensitive content like HTML email bodies
 * that may be included in Cognito validation errors
 */
const sanitizeErrorMessage = (error: unknown): string => {
  if (!(error instanceof Error)) {
    return 'An unexpected error occurred. Please try again.'
  }

  const message = error.message

  // Check if this is a Cognito InvalidParameterException with embedded content
  if (
    message.includes('InvalidParameterException') ||
    message.includes('validation error detected') ||
    message.length > 500
  ) {
    // Return a generic message for validation errors with large payloads
    return 'An unexpected error occurred. Please try again or contact support if the problem persists.'
  }

  return message
}

// Enum for the different steps in the sign in process
export enum SignInStep {
  DONE = 'DONE',
  CONFIRM_SIGN_UP = 'CONFIRM_SIGN_UP',
  RESET_PASSWORD = 'RESET_PASSWORD',
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  CONFIRM_SIGN_IN_WITH_SMS_CODE = 'CONFIRM_SIGN_IN_WITH_SMS_CODE',
  CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE = 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE',
  CONFIRM_SIGN_IN_WITH_TOTP_CODE = 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  CONTINUE_SIGN_IN_WITH_TOTP_SETUP = 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP',
  CONTINUE_SIGN_IN_WITH_MFA_SELECTION = 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION',
  CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION = 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION',
  CONFIRM_SIGN_IN_WITH_EMAIL_CODE = 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
}

// Enum for authentication flow types
export enum AuthFlowType {
  USER_AUTH = 'USER_AUTH',
  CUSTOM_AUTH = 'CUSTOM_AUTH',
  USER_SRP_AUTH = 'USER_SRP_AUTH',
}

// Enum for preferred challenge types in OTP flow
export enum ChallengeType {
  EMAIL_OTP = 'EMAIL_OTP',
  SMS_OTP = 'SMS_OTP',
  PASSWORD = 'PASSWORD',
}

export interface AuthState {
  user: Auth.AuthUser | null
  username: string
  userDetails: User
  isLoading: boolean
  isAuthenticated: boolean
}

export type UserSignUpInput = {
  username: string
  password: string
  email: string
  firstName: string
  lastName: string
}

export type UserSignInInput = {
  username: string
  password: string
}

export interface AuthResult {
  success: boolean
  message?: string
  nextStep?: string
  /**
   * The Cognito username the identifier resolved to, when it differs from what
   * the user typed. Set on the `CONFIRM_SIGN_UP` path reached by entering the
   * *email* of an unconfirmed user, whose email alias Cognito can't resolve
   * yet — callers should route to `/confirm-signup` with this rather than the
   * raw identifier. Both `signIn` (#2407) and `requestOTP` (#2422) produce it.
   */
  resolvedUsername?: string
}

interface AuthActions {
  signIn: (userDetails: UserSignInInput) => Promise<AuthResult>
  signOut: () => Promise<AuthResult>
  signUp: (userDetails: UserSignUpInput) => Promise<AuthResult>
  autoSignIn: () => Promise<Auth.SignInOutput>
  getAccessToken: () => Promise<string>
  getIdToken: () => Promise<string>
  getDroidmapId: () => Promise<string>
  forgotPassword: (username: string) => Promise<AuthResult>
  confirmPassword: (
    username: string,
    code: string,
    newPassword: string,
  ) => Promise<AuthResult>
  requestOTP: (email: string) => Promise<AuthResult>
  verifyOTP: (token: string) => Promise<AuthResult>
}

export type UseAuth = AuthState & AuthActions

export const AuthContext = createContext<UseAuth | undefined>(undefined)

const initialAuthState: AuthState = {
  user: null,
  username: '',
  userDetails: {} as User,
  isLoading: true,
  isAuthenticated: false,
}

/**
 * Cloud auth provider — real Cognito/Amplify flow used in every deployed
 * environment and the default `nx serve web`.
 */
const CloudAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useProvideAuth()

  if (auth.isLoading) {
    return null
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

/**
 * Local-backend auth provider — bypasses Cognito entirely and signs in as a
 * fixed mock user. The locally-running services use mock auth, so no real
 * token is needed. Only active when `VITE_LOCAL_API=true`.
 */
const LocalAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useLocalDevAuth()

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

/**
 * A staff member's read-only view of a customer (droidmap#2651). Wins over
 * both other providers, locally too, so a support-session tab never configures
 * Amplify or signs in as anyone.
 */
const SupportSessionAuthProvider: React.FC<{
  session: SupportSession
  children: ReactNode
}> = ({ session, children }) => (
  <SupportSessionProvider
    session={session}
    renderAuth={(auth) => (
      <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
    )}
  />
)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Both conditions are fixed for the page load (IS_LOCAL_API at build time,
  // the support session when the module loads), so only one provider's hooks
  // ever run. Starting or ending a support session reloads the page.
  if (SUPPORT_SESSION_AT_LOAD) {
    return (
      <SupportSessionAuthProvider session={SUPPORT_SESSION_AT_LOAD}>
        {children}
      </SupportSessionAuthProvider>
    )
  }
  return IS_LOCAL_API ? (
    <LocalAuthProvider>{children}</LocalAuthProvider>
  ) : (
    <CloudAuthProvider>{children}</CloudAuthProvider>
  )
}

export const useAuth = (): UseAuth => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Auth implementation for local-backend dev mode. Returns a stable,
 * already-authenticated session backed by a fixed mock user. All actions are
 * success no-ops; token getters return placeholder strings (the local services
 * use mock auth and ignore the bearer token).
 */
const useLocalDevAuth = (): UseAuth => {
  return useMemo<UseAuth>(() => {
    const ok = async (): Promise<AuthResult> => ({ success: true })
    return {
      user: { username: LOCAL_USER.username } as Auth.AuthUser,
      username: LOCAL_USER.username,
      userDetails: LOCAL_USER,
      isLoading: false,
      isAuthenticated: true,
      signIn: ok,
      signOut: ok,
      signUp: ok,
      autoSignIn: async () =>
        ({
          isSignedIn: true,
          nextStep: { signInStep: SignInStep.DONE },
        }) as unknown as Auth.SignInOutput,
      getAccessToken: async () => 'local-dev-token',
      getIdToken: async () => 'local-dev-id-token',
      getDroidmapId: async () => LOCAL_USER_ID,
      forgotPassword: ok,
      confirmPassword: ok,
      requestOTP: ok,
      verifyOTP: ok,
    }
  }, [])
}

const useProvideAuth = (): UseAuth => {
  const [authState, setAuthState] = useState<AuthState>(initialAuthState)
  const {
    ENV,
    COGNITO_CONFIG: cognitoConfig,
    COGNITO_SERVICE_URL,
  } = useConfig()

  // Configure Amplify once
  useEffect(() => {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: cognitoConfig.UserPoolId,
          userPoolClientId: cognitoConfig.ClientId,
          loginWith: {
            username: true,
            email: true,
          },
          signUpVerificationMethod: 'code',
          userAttributes: {
            email: {
              required: true,
            },
            given_name: {
              required: true,
            },
            family_name: {
              required: true,
            },
          },
        },
      },
    })
  }, [cognitoConfig.UserPoolId, cognitoConfig.ClientId])

  const updateAuthState = useCallback((newState: Partial<AuthState>) => {
    setAuthState((prevState) => ({ ...prevState, ...newState }))
  }, [])

  const getAccessToken = useCallback(async (): Promise<string> => {
    try {
      // Amplify is documented to auto-refresh expired tokens on
      // fetchAuthSession(), but in practice it returns the cached JWT once
      // the tab has been backgrounded long enough that timer throttling
      // delayed its internal refresh. Inspect `exp` ourselves and force a
      // refresh when within the buffer so callers (e.g. tile-service
      // requests) never see a stale token.
      let session = await Auth.fetchAuthSession()
      const expSeconds = session.tokens?.accessToken?.payload?.exp
      const nearExpiry =
        typeof expSeconds !== 'number' ||
        Date.now() >= expSeconds * 1000 - ACCESS_TOKEN_REFRESH_BUFFER_MS
      if (nearExpiry) {
        session = await Auth.fetchAuthSession({ forceRefresh: true })
      }
      return session.tokens?.accessToken.toString() || ''
    } catch (err) {
      console.error('Failed to fetch access token:', err)
      throw new Error('Failed to fetch access token')
    }
  }, [])

  const getIdToken = useCallback(async (): Promise<string> => {
    try {
      const session = await Auth.fetchAuthSession()
      return session.identityId || ''
    } catch (err) {
      console.error('Failed to fetch id token:', err)
      throw new Error('Failed to fetch id token')
    }
  }, [])

  const getDroidmapId = useCallback(async (): Promise<string> => {
    try {
      const session = await Auth.fetchAuthSession()
      return (session.tokens?.accessToken.payload['userId'] as string) || ''
    } catch (err) {
      console.error('Failed to fetch droidmap id:', err)
      throw new Error('Failed to fetch droidmap id')
    }
  }, [])

  const getUserDetails = useCallback(
    async (userId: string): Promise<User> => {
      try {
        const client = createStandaloneUserServiceClient(
          getAccessToken,
          userId,
          StandardOrgIds.SYSTEM,
          ENV,
        )
        const user = await client.getUser({ id: userId })
        return user || null
      } catch (err) {
        console.error('Failed to fetch user details:', err)
        throw new Error('Failed to fetch user details')
      }
    },
    [ENV, getAccessToken],
  )

  const getCurrentUser = useCallback(async () => {
    try {
      const user = await Auth.getCurrentUser()
      const userId = await getDroidmapId()
      const details = await getUserDetails(userId)

      updateAuthState({
        user,
        username: user.username,
        userDetails: details,
        isLoading: false,
        isAuthenticated: true,
      })
    } catch (err) {
      updateAuthState({
        ...initialAuthState,
        isLoading: false,
      })

      if (err instanceof AuthError) {
        // User is not signed in - this is an expected case
        return
      }
      console.error('Failed to fetch current user:', err)
    }
  }, [getDroidmapId, getUserDetails, updateAuthState])

  // Initialize authentication state.
  //
  // `getCurrentUser` awaits before it writes any state, so nothing here runs
  // synchronously — but the compiler inlines locally-defined callbacks into the
  // effect body and then sees the writes, which reads as a cascading render.
  // Awaiting the call inside an async wrapper states the boundary explicitly.
  // It is the same call at the same time; only the analysis changes.
  useEffect(() => {
    void (async () => {
      await getCurrentUser()
    })()
  }, [getCurrentUser])

  const signIn = useCallback(
    async (userDetails: UserSignInInput): Promise<AuthResult> => {
      try {
        const { username, password } = userDetails
        const signInResponse = await Auth.signIn({ username, password })

        switch (signInResponse.nextStep.signInStep) {
          case SignInStep.DONE:
            await getCurrentUser()
            return { success: true }
          case SignInStep.CONFIRM_SIGN_UP:
            return {
              success: false,
              message: 'Email confirmation required',
              nextStep: signInResponse.nextStep.signInStep,
            }
          case SignInStep.RESET_PASSWORD:
            return {
              success: false,
              message: 'Password reset required',
              nextStep: signInResponse.nextStep.signInStep,
            }
          default:
            return {
              success: false,
              message: `Authentication step required: ${signInResponse.nextStep.signInStep}`,
              nextStep: signInResponse.nextStep.signInStep,
            }
        }
      } catch (err) {
        // Cognito can't resolve an unconfirmed user's email alias, so it
        // answers `UserNotFoundException` for the very users the
        // `CONFIRM_SIGN_UP` step above is meant to redirect. Ask the service
        // whether an unconfirmed account owns that email and, if so, take the
        // same redirect with the resolved username. See #2407.
        const resolvedUsername = await tryResolveUnconfirmedUsername(
          err,
          userDetails.username,
          COGNITO_SERVICE_URL,
        )
        if (resolvedUsername) {
          return {
            success: false,
            message: 'Email confirmation required',
            nextStep: SignInStep.CONFIRM_SIGN_UP,
            resolvedUsername,
          }
        }

        return {
          success: false,
          message: sanitizeErrorMessage(err),
        }
      }
    },
    [COGNITO_SERVICE_URL, getCurrentUser],
  )

  const signUp = useCallback(
    async (userDetails: UserSignUpInput): Promise<AuthResult> => {
      const { username, password, email, firstName, lastName } = userDetails
      try {
        const signUpResponse = await Auth.signUp({
          username,
          password,
          options: {
            userAttributes: {
              given_name: firstName,
              family_name: lastName,
              // Cognito matches emails case-sensitively; store one form.
              email: email.trim().toLowerCase(),
            },
            autoSignIn: true,
          },
        })

        switch (signUpResponse.nextStep.signUpStep) {
          case SignInStep.DONE:
            await getCurrentUser()
            return {
              success: true,
              nextStep: signUpResponse.nextStep.signUpStep,
            }
          case SignInStep.CONFIRM_SIGN_UP:
            return {
              success: true,
              nextStep: signUpResponse.nextStep.signUpStep,
            }
          default:
            return {
              success: false,
              message: `Sign up requires next step: ${signUpResponse.nextStep.signUpStep}`,
              nextStep: signUpResponse.nextStep.signUpStep,
            }
        }
      } catch (err) {
        return {
          success: false,
          message: sanitizeErrorMessage(err),
        }
      }
    },
    [getCurrentUser],
  )

  const autoSignIn = useCallback(async () => {
    try {
      const signInOutput = await Auth.autoSignIn()
      if (signInOutput.isSignedIn) {
        await getCurrentUser()
      }
      return signInOutput
    } catch (err) {
      console.error('Failed to auto sign in:', err)
      throw new Error('Failed to auto sign in')
    }
  }, [getCurrentUser])

  const signOut = useCallback(async (): Promise<AuthResult> => {
    try {
      await Auth.signOut()
      updateAuthState({
        ...initialAuthState,
        isLoading: false,
      })
      return { success: true }
    } catch (err) {
      return {
        success: false,
        message: sanitizeErrorMessage(err),
      }
    }
  }, [updateAuthState])

  const forgotPassword = useCallback(
    async (username: string): Promise<AuthResult> => {
      try {
        await Auth.resetPassword({ username })
        return { success: true }
      } catch (err) {
        return {
          success: false,
          message: sanitizeErrorMessage(err),
        }
      }
    },
    [],
  )

  const confirmPassword = useCallback(
    async (
      username: string,
      confirmationCode: string,
      newPassword: string,
    ): Promise<AuthResult> => {
      try {
        await Auth.confirmResetPassword({
          username,
          confirmationCode,
          newPassword,
        })
        return { success: true }
      } catch (err) {
        return {
          success: false,
          message: sanitizeErrorMessage(err),
        }
      }
    },
    [],
  )

  const requestOTP = useCallback(
    async (email: string): Promise<AuthResult> => {
      try {
        const signInResponse = await Auth.signIn({
          username: email,
          options: {
            authFlowType: AuthFlowType.USER_AUTH,
            preferredChallengeType: ChallengeType.EMAIL_OTP,
          },
        })

        if (
          signInResponse.nextStep.signInStep ===
          SignInStep.CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION
        ) {
          const confirmSignInResponse = await Auth.confirmSignIn({
            challengeResponse: ChallengeType.EMAIL_OTP,
          })

          if (
            confirmSignInResponse.nextStep.signInStep ===
            SignInStep.CONFIRM_SIGN_IN_WITH_EMAIL_CODE
          ) {
            return {
              success: true,
            }
          }
        }

        return {
          success: false,
          message: 'Failed to send verification code',
        }
      } catch (err) {
        // Requesting a code is a `signIn` too, so it hits the same alias
        // mechanic as the password path: an unconfirmed user's email doesn't
        // resolve yet, and Cognito answers `UserNotFoundException`. Ask the
        // service whether an unconfirmed account owns that email and, if so,
        // hand the caller the same `CONFIRM_SIGN_UP` redirect the password form
        // takes. See #2422.
        const resolvedUsername = await tryResolveUnconfirmedUsername(
          err,
          email,
          COGNITO_SERVICE_URL,
        )
        if (resolvedUsername) {
          return {
            success: false,
            message: 'Email confirmation required',
            nextStep: SignInStep.CONFIRM_SIGN_UP,
            resolvedUsername,
          }
        }

        return {
          success: false,
          message: sanitizeErrorMessage(err),
        }
      }
    },
    [COGNITO_SERVICE_URL],
  )

  const verifyOTP = useCallback(
    async (token: string): Promise<AuthResult> => {
      try {
        const signInResponse = await Auth.confirmSignIn({
          challengeResponse: token,
        })

        if (signInResponse.isSignedIn) {
          await getCurrentUser()
          return { success: true }
        }

        return {
          success: false,
          message: 'Invalid or expired verification code',
        }
      } catch (err) {
        return {
          success: false,
          message: sanitizeErrorMessage(err),
        }
      }
    },
    [getCurrentUser],
  )

  const authActions = useMemo(
    () => ({
      signIn,
      signOut,
      signUp,
      autoSignIn,
      getAccessToken,
      getIdToken,
      getDroidmapId,
      forgotPassword,
      confirmPassword,
      requestOTP,
      verifyOTP,
    }),
    [
      signIn,
      signOut,
      signUp,
      autoSignIn,
      getAccessToken,
      getIdToken,
      getDroidmapId,
      forgotPassword,
      confirmPassword,
      requestOTP,
      verifyOTP,
    ],
  )

  return {
    ...authState,
    ...authActions,
  }
}
